/* eslint-disable prefer-rest-params */ /* eslint-disable no-restricted-syntax */ /*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */ // Portions of the code in this file are based on code from react.
// Original licensing for the following can be found in the
// NOTICE file in the root directory of this source tree.
// See https://github.com/facebook/react/tree/cc7c1aece46a6b69b41958d731e0fd27c94bfc6c/packages/react-interactions
import { useEffect, useState } from 'react';
import { useIsSSR } from '../ssr';
import { isMac, isVirtualClick } from '../utils';
const getOwnerDocument = (el)=>{
    var _el_ownerDocument;
    return (_el_ownerDocument = el === null || el === void 0 ? void 0 : el.ownerDocument) !== null && _el_ownerDocument !== void 0 ? _el_ownerDocument : document;
};
const getOwnerWindow = (el)=>{
    if (el && 'window' in el && el.window === el) {
        return el;
    }
    const doc = getOwnerDocument(el);
    return doc.defaultView || window;
};
let currentModality = null;
const changeHandlers = new Set();
const hasSetupGlobalListeners = new Map();
let hasEventBeforeFocus = false;
let hasBlurredWindowRecently = false;
// Only Tab or Esc keys will make focus visible on text input elements
const FOCUS_VISIBLE_INPUT_KEYS = {
    Tab: true,
    Escape: true
};
function triggerChangeHandlers(modality, e) {
    if (!e) {
        return;
    }
    for (const handler of changeHandlers){
        handler(modality, e);
    }
}
/**
 * Helper function to determine if a KeyboardEvent is unmodified and could make keyboard focus styles visible.
 */ function isValidKey(e) {
    // Control and Shift keys trigger when navigating back to the tab with keyboard.
    return !(e.metaKey || !isMac() && e.altKey || e.ctrlKey || e.key === 'Control' || e.key === 'Shift' || e.key === 'Meta');
}
function handleKeyboardEvent(e) {
    hasEventBeforeFocus = true;
    if (isValidKey(e)) {
        currentModality = 'keyboard';
        triggerChangeHandlers('keyboard', e);
    }
}
function handlePointerEvent(e) {
    currentModality = 'pointer';
    if (e.type === 'mousedown' || e.type === 'pointerdown') {
        hasEventBeforeFocus = true;
        triggerChangeHandlers('pointer', e);
    }
}
function handleClickEvent(e) {
    if (isVirtualClick(e)) {
        hasEventBeforeFocus = true;
        currentModality = 'virtual';
    }
}
function handleFocusEvent(e) {
    // Firefox fires two extra focus events when the user first clicks into an iframe:
    // first on the window, then on the document. We ignore these events so they don't
    // cause keyboard focus rings to appear.
    const windowObject = getOwnerWindow(e.target);
    const documentObject = getOwnerDocument(e.target);
    if (e.target === windowObject || e.target === documentObject) {
        return;
    }
    // If a focus event occurs without a preceding keyboard or pointer event, switch to virtual modality.
    // This occurs, for example, when navigating a form with the next/previous buttons on iOS.
    if (!hasEventBeforeFocus && !hasBlurredWindowRecently) {
        currentModality = 'virtual';
        triggerChangeHandlers('virtual', e);
    }
    hasEventBeforeFocus = false;
    hasBlurredWindowRecently = false;
}
function handleWindowBlur() {
    // When the window is blurred, reset state. This is necessary when tabbing out of the window,
    // for example, since a subsequent focus event won't be fired.
    hasEventBeforeFocus = false;
    hasBlurredWindowRecently = true;
}
function tearDownWindowFocusTracking(element) {
    const windowObject = getOwnerWindow(element);
    const documentObject = getOwnerDocument(element);
    if (!hasSetupGlobalListeners.has(windowObject)) {
        return;
    }
    documentObject.removeEventListener('keydown', handleKeyboardEvent, true);
    documentObject.removeEventListener('keyup', handleKeyboardEvent, true);
    documentObject.removeEventListener('click', handleClickEvent, true);
    windowObject.removeEventListener('focus', handleFocusEvent, true);
    windowObject.removeEventListener('blur', handleWindowBlur, false);
    if (typeof PointerEvent !== 'undefined') {
        documentObject.removeEventListener('pointerdown', handlePointerEvent, true);
        documentObject.removeEventListener('pointermove', handlePointerEvent, true);
        documentObject.removeEventListener('pointerup', handlePointerEvent, true);
    } else {
        documentObject.removeEventListener('mousedown', handlePointerEvent, true);
        documentObject.removeEventListener('mousemove', handlePointerEvent, true);
        documentObject.removeEventListener('mouseup', handlePointerEvent, true);
    }
    hasSetupGlobalListeners.delete(windowObject);
}
/**
 * Setup global event listeners to control when keyboard focus style should be visible.
 */ function setupGlobalFocusEvents(element) {
    if (typeof window === 'undefined' || hasSetupGlobalListeners.get(getOwnerWindow(element))) {
        return;
    }
    const windowObject = getOwnerWindow(element);
    const documentObject = getOwnerDocument(element);
    // Programmatic focus() calls shouldn't affect the current input modality.
    // However, we need to detect other cases when a focus event occurs without
    // a preceding user event (e.g. screen reader focus). Overriding the focus
    // method on HTMLElement.prototype is a bit hacky, but works.
    const focus = windowObject.HTMLElement.prototype.focus;
    windowObject.HTMLElement.prototype.focus = function() {
        hasEventBeforeFocus = true;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        focus.apply(this, arguments);
    };
    documentObject.addEventListener('keydown', handleKeyboardEvent, true);
    documentObject.addEventListener('keyup', handleKeyboardEvent, true);
    documentObject.addEventListener('click', handleClickEvent, true);
    // Register focus events on the window so they are sure to happen
    // before React's event listeners (registered on the document).
    window.addEventListener('focus', handleFocusEvent, true);
    window.addEventListener('blur', handleWindowBlur, false);
    // We register different listeners depending on touch/mouse devices, tearing these down respectively
    if (typeof PointerEvent !== 'undefined') {
        documentObject.addEventListener('pointerdown', handlePointerEvent, true);
        documentObject.addEventListener('pointermove', handlePointerEvent, true);
        documentObject.addEventListener('pointerup', handlePointerEvent, true);
    } else {
        documentObject.addEventListener('mousedown', handlePointerEvent, true);
        documentObject.addEventListener('mousemove', handlePointerEvent, true);
        documentObject.addEventListener('mouseup', handlePointerEvent, true);
    }
    // Emitted when UI layer and accessory layers are closed, when we switch to another component
    windowObject.addEventListener('unload', ()=>{
        tearDownWindowFocusTracking(element);
    }, {
        once: true
    });
    // Emitted from chrome when the user navigates away from page. We use both `unload` and `beforeunload` to maximize browser compatibility
    windowObject.addEventListener('beforeunload', ()=>{
        tearDownWindowFocusTracking(element);
    }, {
        once: true
    });
    hasSetupGlobalListeners.set(windowObject, true);
}
export const setupFocus = (element)=>{
    const documentObject = getOwnerDocument(element);
    if (documentObject.readyState !== 'loading') {
        setupGlobalFocusEvents(element);
    } else {
        documentObject.addEventListener('DOMContentLoaded', ()=>setupGlobalFocusEvents(element));
    }
};
// Server-side rendering does not have the document object defined
if (typeof document !== 'undefined') {
    setupFocus();
}
/**
 * If true, keyboard focus is visible.
 */ export function isFocusVisible() {
    return currentModality !== 'pointer';
}
export function getInteractionModality() {
    return currentModality;
}
export function setInteractionModality(modality) {
    currentModality = modality;
    triggerChangeHandlers(modality, null);
}
/**
 * Keeps state of the current modality.
 */ export function useInteractionModality() {
    setupGlobalFocusEvents();
    const [modality, setModality] = useState(currentModality);
    useEffect(()=>{
        const handler = ()=>{
            setModality(currentModality);
        };
        changeHandlers.add(handler);
        return ()=>{
            changeHandlers.delete(handler);
        };
    }, []);
    return useIsSSR() ? null : modality;
}
/**
 * If this is attached to text input component, return if the event is a focus event (Tab/Escape keys pressed) so that
 * focus visible style can be properly set.
 */ function isKeyboardFocusEvent(isTextInput, modality, e) {
    const IKeyboardEvent = typeof window !== 'undefined' ? getOwnerWindow(e === null || e === void 0 ? void 0 : e.target).KeyboardEvent : KeyboardEvent;
    return !(isTextInput && modality === 'keyboard' && e instanceof IKeyboardEvent && !FOCUS_VISIBLE_INPUT_KEYS[e.key]);
}
/**
 * Listens for trigger change and reports if focus is visible (i.e., modality is not pointer).
 */ export function useFocusVisibleListener(fn, // eslint-disable-next-line @typescript-eslint/no-explicit-any
deps, opts) {
    setupGlobalFocusEvents();
    useEffect(()=>{
        const handler = (modality, e)=>{
            if (!isKeyboardFocusEvent(Boolean(opts === null || opts === void 0 ? void 0 : opts.isTextInput), modality, e)) {
                return;
            }
            fn(isFocusVisible());
        };
        changeHandlers.add(handler);
        return ()=>{
            changeHandlers.delete(handler);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
/**
 * Manages focus visible state for the page, and subscribes individual components for updates.
 */ export function useFocusVisible(props = {}) {
    const { isTextInput, autoFocus } = props;
    const [isFocusVisibleState, setFocusVisible] = useState(autoFocus || isFocusVisible());
    useFocusVisibleListener((isFocusVisible)=>{
        setFocusVisible(isFocusVisible);
    }, [
        isTextInput
    ], {
        isTextInput
    });
    return {
        isFocusVisible: isFocusVisibleState
    };
}

//# sourceMappingURL=useFocusVisible.js.map